import React, { useEffect, useRef, useState } from 'react';
import { FormControl, FormControlLabel, InputLabel, Radio, RadioGroup } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { FormRadioInputProps } from './FormInputProps';
import { Editor } from '@tinymce/tinymce-react';
import { FormLabel } from './FormLabel';
import { set } from 'lodash';
import { CONTENTMEDIAS_LIST_QUERY } from 'pages/content/gql';
import { useQuery } from '@apollo/client';
import { EContentMediaArea } from '__generated__/graphql';

interface FormRichTextEditorProps {
  name: string;
  options: any;
  control: any;
  label?: string | null | undefined;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  /** Space the "insert image" picker's media list is scoped to. Only SEM/BOTH-tagged media is offered - hotel
   * photos (SEMINARGO) aren't relevant inside a template. No spaceId means the picker stays empty. */
  spaceId?: number | null;
}

/**
 * Rich text editor component using TinyMCE.
 * Integrates with content media library for image insertion.
 * Preserves cursor position during updates to prevent jumping.
 */
export const FormInputRichText = ({ name, control, label, disabled, options, placeholder, spaceId }: FormRichTextEditorProps) => {
  const { watch, setValue, getValues } = useFormContext();
  const [value, setValueState] = useState<any>(watch(name));
  const [curPos, setCurPos] = useState<any>(null);
  const [editorLoaded, setEditorLoaded] = useState(false);

  const { loading: mediaLoading, error: mediaError, data: mediaData } = useQuery(CONTENTMEDIAS_LIST_QUERY, {
    variables: { spaceId, areas: [EContentMediaArea.SEM, EContentMediaArea.BOTH] },
    skip: !spaceId,
  });

  const editorRef = useRef<any>(null);

  useEffect(() => {
    const ed = editorRef.current;
    if (ed && !ed.removed && curPos) {
      ed.selection.moveToBookmark(curPos);
    }
  }, [value, curPos]);

  if (mediaLoading) {
    return <div>Loading...</div>;
  }

  return (
    <FormControl fullWidth>
      {label && (
        <>
          <InputLabel>{label}</InputLabel>
          <br />
          <br />
        </>
      )}
      <Controller
        rules={{ required: true }}
        control={control}
        name={name}
        render={({ field }) => (
          <>
            <Editor
              disabled={disabled}
              tinymceScriptSrc="https://cdn.jsdelivr.net/npm/tinymce@6.7.0/tinymce.min.js"
              onInit={(_evt: any, ed: any) => {
                editorRef.current = ed;
              }}
              onEditorChange={val => {
                if (editorLoaded) {
                  const ed = editorRef.current;
                  if (ed && !ed.removed) {
                    setCurPos(ed.selection.getBookmark(2));
                  }
                  setValue(name, val);
                  setValueState(val);
                  field.onChange(val);
                } else {
                  setEditorLoaded(true);
                }
              }}
              onChange={() => {
                const ed = editorRef.current;
                if (!ed || ed.removed) return;
                setCurPos(ed.selection.getBookmark(2));
                const content = ed.getContent();
                setValue(name, content);
                setValueState(content);
                field.onChange(content);
                if (ed.queryCommandState('ToggleToolbarDrawer')) {
                  ed.execCommand('ToggleToolbarDrawer');
                }
              }}
              initialValue={getValues(name) || ''}
              init={Object.assign(
                {},
                {
                  toolbar_mode: 'wrap',
                  placeholder: placeholder,
                  language: 'de',
                  language_url: 'https://cdn.jsdelivr.net/npm/tinymce-lang/langs/de.js',
                  height: 350,
                  menubar: false,
                  remove_linebreaks: false,
                  cleanup: false,
                  verify_html: false,
                  allow_html_in_named_anchor: true,
                  valid_elements: '*[*]',
                  extended_valid_elements: '*[*]',
                  image_list: (mediaData?.listContentMedia?.medias || []).map((media: any) => ({
                    title: media.name,
                    value: media.previewUrl,
                  })),
                  plugins: [
                    'code',
                    'table',
                    'link',
                    'image',
                    'insertdatetime',
                    'lists',
                    'advlist',
                    'autolink',
                    'table',
                    'textcolor',
                    'colorpicker',
                    'advlist autolink lists link image charmap print preview anchor',
                    'searchreplace visualblocks code fullscreen',
                    'insertdatetime media table paste code help wordcount',
                  ],
                  toolbar:
                    'formatselect | ' +
                    'bold italic underline forecolor | alignleft aligncenter ' +
                    'alignright alignjustify | bullist numlist outdent indent | link image table | ' +
                    'code | removeformat',
                  content_style: 'body { font-family:Arial,sans-serif; font-size:14px }',
                },
                options,
              )}
            />
          </>
        )}
      />
    </FormControl>
  );
};
